<script setup lang="ts">
import type { PropType, Ref } from 'vue';
import { onMounted, ref, toRefs, watch } from 'vue';
import type {
  RegistrationProfileData,
  RegistrationProfileErrors,
} from '~/pages/registration/components/registration-stages.vue';
import BaseCheckbox from '~/shared/components/form/base-checkbox.vue';
import BaseFormGroup from '~/shared/components/form/base-form-group.vue';
import BaseInput from '~/shared/components/form/input/base-input.vue';
import CheckboxFormGroup from '~/shared/components/form/checkbox-form-group.vue';
import BaseClickableText from '~/shared/components/buttons/base-clickable-text.vue';

const props = defineProps({
  errors: {
    type: Object as PropType<RegistrationProfileErrors>,
    required: true,
  },
  isHidden: {
    type: Boolean,
    default: false,
  },
});

const { isHidden } = toRefs(props);

const model = defineModel({
  type: Object as PropType<RegistrationProfileData>,
  required: true,
});

const firstNameInputRef = ref<{ inputRef: Ref<HTMLInputElement | null> } | null>(null);

watch(isHidden, () => {
  if (!isHidden.value) {
    firstNameInputRef.value?.inputRef?.focus();
  }
});

onMounted(() => {
  if (firstNameInputRef.value?.inputRef) firstNameInputRef.value?.inputRef?.focus();
});
</script>

<template>
  <div class="flex flex-col min-h-89">
    <div class="flex flex-col gap-8 lg:gap-10">
      <div class="flex flex-col md:flex-row gap-8 md:gap-8 lg:gap-10">
        <BaseFormGroup class="flex-1" name="firstName" :error="errors.firstName" label="First Name">
          <BaseInput
            id="firstName"
            ref="firstNameInputRef"
            v-model="model.firstName"
            name="firstName"
            placeholder="Your Name"
            :error="errors.firstName"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>

        <BaseFormGroup class="flex-1" name="lastName" :error="errors.lastName" label="Last Name">
          <BaseInput
            id="lastName"
            v-model="model.lastName"
            name="lastName"
            placeholder="Your Last Name"
            :error="errors.lastName"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>
      </div>

      <div class="flex flex-col md:flex-row gap-8 md:gap-8 lg:gap-10">
        <BaseFormGroup class="flex-1" name="phone" :error="errors.phone" label="Phone Number">
          <BaseInput
            id="phone"
            v-model="model.phone"
            name="phone"
            mask="+1 (###) ###-####"
            placeholder="+1 (234)-567-8900"
            :error="errors.phone"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>

        <BaseFormGroup class="flex-1" name="email" :error="errors.email" label="Email">
          <BaseInput
            id="email"
            v-model="model.email"
            name="email"
            placeholder="example@gmail.com"
            :error="errors.email"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>
      </div>

      <div class="flex flex-col md:flex-row gap-8 md:gap-8 lg:gap-10">
        <BaseFormGroup class="flex-1" name="password" :error="errors.password" label="Password">
          <BaseInput
            id="password"
            v-model="model.password"
            type="password"
            name="password"
            placeholder="Enter Your Password"
            :error="errors.password"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>

        <BaseFormGroup
          class="flex-1"
          name="password-confirmation"
          :error="errors.passwordConfirmation"
          label="Password Confirmation"
        >
          <BaseInput
            id="password-confirmation"
            v-model="model.passwordConfirmation"
            type="password"
            name="password-confirmation"
            placeholder="Confirm Your Password"
            :error="errors.password"
            :non-focusable="isHidden"
          />
        </BaseFormGroup>
      </div>

      <div class="flex justify-center">
        <CheckboxFormGroup name="accept-terms" :error="errors.acceptTerms">
          <template #label="{ inputName }">
            <label :for="inputName" class="text-left cursor-pointer">
              I accept
              <BaseClickableText text="terms and conditions" :to="{ name: 'terms-and-conditions' }" new-tab prefetch />
            </label>
          </template>

          <BaseCheckbox
            id="accept-terms"
            v-model="model.acceptTerms"
            name="accept-terms"
            :error="errors.acceptTerms.trim()"
            :non-focusable="isHidden"
          />
        </CheckboxFormGroup>
      </div>
    </div>
  </div>
</template>
